<template>
  <Form
    :form-id="formId"
    :initial-values="initialValues"
    action="#"
    class="section section-row div-5"
    :class="{ dirty }"
  >
    <HiddenInput input-id="id" />
    <VPPriceInput
      ref="amountRef"
      :disabled="disabled"
      input-id="amount"
      placeholder="Add amount"
      class="text-left"
      hide-empty-input
    />
    <div :class="{ 'has-value': dueHasType }">
      <VPDropdown
        input-id="due"
        placeholder="Choose due type"
        :options="customExpenseDueOptions"
        @change="onTypeChange"
      />
    </div>
    <div :class="{ 'has-value': deductedHasType }">
      <VPDropdown
        input-id="deductedType"
        placeholder="Choose deducted type"
        :options="customExpenseDeductedTypeOptions"
        @change="onTypeChange"
      />
    </div>
    <VPInput
      ref="descriptionRef"
      type="text"
      :disabled="disabled"
      input-id="description"
      placeholder="Add description"
      class="double-f"
    />
  </Form>
</template>

<script setup lang="ts">
import { Form, HiddenInput, useFormsStore } from 'vuniform';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import VPPriceInput from '@/components/form/VPPriceInput.vue';
import VPInput from '@/components/form/VPInput.vue';
import { CustomExpenseDueEnum, CustomExpenseDeductedTypeEnum } from '@/generated/graphql';
import VPDropdown from '@/components/form/VPDropdown.vue';

const formsStore = useFormsStore();

const descriptionRef = ref<VPInput>(null);
const dueRef = ref<VPDropdown>(null);
const deductedTypeRef = ref<VPDropdown>(null);
const amountRef = ref<VPPriceInput>(null);

interface Props {
  formId: string;
  initialValues: unknown;
  disabled?: boolean;
}

const props = defineProps<Props>();

const customExpenseDueOptions = [
  {
    name: 'Fees due to Artist',
    value: CustomExpenseDueEnum.ToArtist,
  },
  {
    name: 'Fees due to Venue',
    value: CustomExpenseDueEnum.ToVenue,
  },
];

const dueHasType = computed(() => !!formsStore.fieldGetValue(props.formId, 'due'));

const customExpenseDeductedTypeOptions = [
  {
    name: 'NET',
    value: CustomExpenseDeductedTypeEnum.Net,
  },
  {
    name: 'GROSS',
    value: CustomExpenseDeductedTypeEnum.Gross,
  },
];

const deductedHasType = computed(() => !!formsStore.fieldGetValue(props.formId, 'deductedType'));

const emit = defineEmits<{
  (e: 'blur'): void;
  (e: 'save'): void;
  (e: 'clear'): void;
}>();

const refs = [descriptionRef, dueRef, deductedTypeRef, amountRef];

const activeElement = ref<EventTarget | null>(null);

const focusChanged = (e: Event) => {
  activeElement.value = e.target;
};

onMounted(() => {
  document.addEventListener('focusin', focusChanged);
});
onBeforeUnmount(() => {
  document.removeEventListener('focusin', focusChanged);
});

const hasFocus = computed(() =>
  refs.some(r => r.value?.inputRef && activeElement.value && r.value?.inputRef === activeElement.value),
);

const hasValue = computed(() => {
  return ['amount', 'description'].some(f => formsStore.fieldGetValue(props.formId, f));
});

watch(hasFocus, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    if (hasValue.value) {
      emit('blur');
    } else {
      emit('clear');
    }
  }
});

const dirty = computed(() => formsStore.isFormDirty(props.formId));

const onTypeChange = () => {
  emit('save');
};
</script>
